/* End-of-year promotion page: class-by-class status matrix, preview, execute. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { yearEndApi, classesApi, schoolYearsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  GraduationCap, Play, Warning, ArrowClockwise, Archive,
  CheckCircle, FloppyDisk, Info, FilePdf, MicrosoftExcelLogo,
} from '@phosphor-icons/react';

const STATUSES = [
  { key: 'promu', label: 'Promu(e)', color: 'text-emerald-700 bg-emerald-50' },
  { key: 'admis', label: 'Admis(e)', color: 'text-blue-700 bg-blue-50' },
  { key: 'redoublé', label: 'Redoublé', color: 'text-amber-700 bg-amber-50' },
  { key: 'transféré', label: 'Transféré', color: 'text-slate-700 bg-slate-100' },
  { key: 'quitté', label: 'Quitté', color: 'text-red-700 bg-red-50' },
];

const StatusBadge = ({ status }) => {
  const cfg = STATUSES.find((s) => s.key === status);
  if (!cfg) return <span className="text-xs text-[#94A3B8]">Non décidé</span>;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

const YearEndPromotion = () => {
  const [schoolYear, setSchoolYear] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [classData, setClassData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rowState, setRowState] = useState({}); // {student_id: {status, next_class}}
  const [saving, setSaving] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Bootstrap: load active school year + classes
  useEffect(() => {
    (async () => {
      try {
        const [{ data: cfg }, { data: cls }] = await Promise.all([
          schoolYearsApi.getActive(),
          classesApi.getAll(),
        ]);
        setSchoolYear(cfg?.name || '2026-2027');
        setClasses((cls || []).map((c) => c.name).filter(Boolean).sort());
      } catch {
        setSchoolYear('2026-2027');
      }
    })();
  }, []);

  const loadSummary = useCallback(async () => {
    if (!schoolYear) return;
    try {
      const { data } = await yearEndApi.summary(schoolYear);
      setSummary(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur chargement résumé'); }
  }, [schoolYear]);

  const loadClass = useCallback(async () => {
    if (!schoolYear || !selectedClass) return;
    try {
      const { data } = await yearEndApi.classView(selectedClass, schoolYear);
      setClassData(data);
      const state = {};
      data.students.forEach((s) => {
        state[s.id] = {
          status: s.status || '',
          next_class: s.next_class || '',
          notes: s.notes || '',
        };
      });
      setRowState(state);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur chargement classe'); }
  }, [schoolYear, selectedClass]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadClass(); }, [loadClass]);

  const updateRow = (sid, patch) => {
    setRowState((prev) => ({ ...prev, [sid]: { ...prev[sid], ...patch } }));
  };

  const applyBulk = (status) => {
    if (!classData) return;
    const next = {};
    classData.students.forEach((s) => {
      next[s.id] = {
        ...rowState[s.id],
        status,
        // For promoted students, auto-fill from suggestion when available
        next_class:
          status === 'promu' || status === 'admis'
            ? (classData.auto_next_class || rowState[s.id]?.next_class || '')
            : status === 'redoublé'
              ? classData.class_name
              : '',
      };
    });
    setRowState(next);
    toast.success(`Statut « ${status} » appliqué à ${classData.students.length} élève(s) — pensez à sauvegarder.`);
  };

  const save = async () => {
    if (!classData) return;
    setSaving(true);
    try {
      const items = classData.students
        .map((s) => rowState[s.id])
        .map((r, i) => ({
          student_id: classData.students[i].id,
          status: r?.status || '',
          next_class: r?.next_class || null,
          notes: r?.notes || null,
        }))
        .filter((it) => it.status); // only save decided ones
      if (!items.length) { toast.warning('Aucun statut à enregistrer.'); return; }
      const { data } = await yearEndApi.setStatuses({
        school_year: schoolYear, items,
      });
      toast.success(`${data.upserted} statut(s) enregistré(s).`);
      await Promise.all([loadClass(), loadSummary()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally { setSaving(false); }
  };

  const runPreview = async () => {
    try {
      const { data } = await yearEndApi.preview(schoolYear);
      setPreview(data);
      setPreviewOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur preview'); }
  };

  const runExecute = async () => {
    if (!window.confirm(`Exécuter la promotion pour ${schoolYear} ? Les élèves seront déplacés vers l'année suivante. Rollback disponible ensuite.`)) return;
    setExecuting(true);
    try {
      const { data } = await yearEndApi.execute(schoolYear);
      toast.success(`Promotion exécutée : ${data.executed.promoted} promus, ${data.executed.held} redoublants, ${data.executed.archived} archivés.`);
      setPreviewOpen(false);
      await loadSummary();
      await loadClass();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur exécution');
    } finally { setExecuting(false); }
  };

  const runRollback = async () => {
    if (!window.confirm(`Annuler la promotion de ${schoolYear} et remettre tous les élèves dans leur classe d'origine ?`)) return;
    try {
      const { data } = await yearEndApi.rollback(schoolYear);
      toast.success(`Rollback : ${data.rolled_back} élève(s) restaurés.`);
      await loadSummary();
      await loadClass();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur rollback'); }
  };

  const downloadRoster = async (kind) => {
    try {
      const fn = kind === 'pdf' ? yearEndApi.rostersPdf : yearEndApi.rostersXlsx;
      const { data } = await fn(schoolYear);
      const mime = kind === 'pdf' ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `repartition_${schoolYear}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur téléchargement');
    }
  };


  const decisionOptions = classData?.decision_options || [];
  const needsDecision = !!classData?.requires_decision;

  const kpiCards = useMemo(() => {
    if (!summary) return null;
    const s = summary.counts_by_status;
    return (
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KPICard label="Élèves" value={summary.total_students} tone="default" testid="kpi-total" />
        <KPICard label="Décidés" value={summary.total_decided} tone="ok" testid="kpi-decided" />
        <KPICard label="En attente" value={summary.total_pending} tone={summary.total_pending ? 'warn' : 'ok'} testid="kpi-pending" />
        <KPICard label="Promus/Admis" value={(s.promu || 0) + (s.admis || 0)} tone="ok" testid="kpi-promoted" />
        <KPICard label="Redoublants" value={s.redoublé || 0} tone="warn" testid="kpi-repeat" />
        <KPICard label="Transf./Quittés" value={(s.transféré || 0) + (s.quitté || 0)} tone="danger" testid="kpi-leave" />
      </div>
    );
  }, [summary]);

  return (
    <div className="space-y-5" data-testid="year-end-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <GraduationCap size={26} weight="duotone" className="text-[#0052FF]" />
            Promotion de fin d&apos;année
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Décidez du sort de chaque élève, prévisualisez puis appliquez. Rollback possible.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div>
            <Label className="text-xs">Année</Label>
            <Input
              value={schoolYear}
              onChange={(e) => setSchoolYear(e.target.value)}
              placeholder="2026-2027"
              className="w-32"
              data-testid="ye-year-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={runPreview}
            className="border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF]/5"
            data-testid="ye-preview-btn"
          >
            <Play size={14} className="mr-1.5" /> Prévisualiser
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadRoster('pdf')}
            className="border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF]/5"
            data-testid="ye-roster-pdf-btn"
          >
            <FilePdf size={14} className="mr-1.5" weight="duotone" /> Répartition PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadRoster('xlsx')}
            className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
            data-testid="ye-roster-xlsx-btn"
          >
            <MicrosoftExcelLogo size={14} className="mr-1.5" weight="duotone" /> Excel
          </Button>
          <Button
            variant="outline"
            onClick={runRollback}
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid="ye-rollback-btn"
          >
            <ArrowClockwise size={14} className="mr-1.5" /> Annuler la promotion
          </Button>
        </div>
      </div>

      {/* KPIs */}
      {kpiCards}

      {/* Class matrix */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            Décisions par classe
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-48" data-testid="ye-class-select">
                <SelectValue placeholder="Choisir une classe" />
              </SelectTrigger>
              <SelectContent>
                {classes.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {!selectedClass ? (
            <div className="text-sm text-[#64748B] py-8 text-center">
              Sélectionnez une classe pour afficher les élèves.
            </div>
          ) : !classData ? (
            <div className="text-sm text-[#64748B] py-8 text-center">Chargement…</div>
          ) : (
            <>
              <div className={`text-xs p-3 rounded-lg flex items-start gap-2 ${
                needsDecision ? 'bg-amber-50 border border-amber-200 text-amber-800'
                              : 'bg-blue-50 border border-blue-200 text-blue-800'
              }`} data-testid="ye-class-hint">
                <Info size={14} className="mt-0.5 shrink-0" weight="duotone" />
                <span><b>{classData.class_name}</b> — {classData.reason}
                  {classData.auto_next_class && ` (classe cible : ${classData.auto_next_class})`}
                </span>
              </div>

              {/* Bulk actions */}
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs text-[#64748B]">Appliquer à toute la classe :</span>
                {STATUSES.map((s) => (
                  <Button
                    key={s.key} variant="outline" size="sm"
                    onClick={() => applyBulk(s.key)}
                    data-testid={`ye-bulk-${s.key}`}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>

              {/* Students table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Élève</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Classe cible</TableHead>
                    <TableHead>Actuel</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {classData.students.map((s) => {
                    const r = rowState[s.id] || {};
                    const showTarget = r.status === 'promu' || r.status === 'admis';
                    return (
                      <TableRow key={s.id} data-testid={`ye-row-${s.id}`}>
                        <TableCell className="font-manrope">
                          <div className="font-medium">{s.last_name} {s.first_name}</div>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={r.status || ''}
                            onValueChange={(v) => updateRow(s.id, {
                              status: v,
                              next_class: v === 'redoublé' ? classData.class_name
                                : (v === 'promu' || v === 'admis')
                                  ? (classData.auto_next_class || r.next_class || '')
                                  : '',
                            })}
                          >
                            <SelectTrigger className="h-8 w-40" data-testid={`ye-status-${s.id}`}>
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((st) => (
                                <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {showTarget && needsDecision ? (
                            <Select
                              value={r.next_class || ''}
                              onValueChange={(v) => updateRow(s.id, { next_class: v })}
                            >
                              <SelectTrigger className="h-8 w-40" data-testid={`ye-target-${s.id}`}>
                                <SelectValue placeholder="Choisir" />
                              </SelectTrigger>
                              <SelectContent>
                                {decisionOptions.map((o) => (
                                  <SelectItem key={o} value={o}>{o}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : showTarget ? (
                            <Input
                              value={r.next_class || ''}
                              onChange={(e) => updateRow(s.id, { next_class: e.target.value })}
                              className="h-8 w-32"
                              data-testid={`ye-target-${s.id}`}
                            />
                          ) : r.status === 'redoublé' ? (
                            <span className="text-xs text-[#64748B]">↺ {classData.class_name}</span>
                          ) : (
                            <StatusBadge status={r.status} />
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-[#64748B]">{s.current_class}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <div className="flex justify-end">
                <Button
                  onClick={save} disabled={saving}
                  className="bg-[#0052FF] hover:bg-[#0052FF]/90"
                  data-testid="ye-save-btn"
                >
                  <FloppyDisk size={14} className="mr-1.5" />
                  {saving ? 'Enregistrement…' : 'Enregistrer les statuts'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Play size={18} className="text-[#0052FF]" weight="duotone" />
              Prévisualisation — {schoolYear} → {preview?.next_school_year}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-center">
                {['promote', 'hold', 'archive', 'graduate', 'pending_decision', 'missing_status'].map((k) => (
                  <div key={k} className="p-2 border border-[#E2E8F0] rounded text-xs">
                    <div className="text-[10px] uppercase text-[#94A3B8]">{k.replace('_', ' ')}</div>
                    <div className="font-bold text-lg">{preview.counts[k]}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#94A3B8]">
                ⚠ {preview.counts.pending_decision} en attente de décision et {preview.counts.missing_status}
                {' '}sans statut ne seront pas traités.
              </p>
              {preview.actions.pending_decision.length > 0 && (
                <div className="text-xs">
                  <b>À décider :</b>{' '}
                  {preview.actions.pending_decision.slice(0, 5).map((a) => a.name).join(', ')}
                  {preview.actions.pending_decision.length > 5 && '…'}
                </div>
              )}
              {preview.actions.archive.length > 0 && (
                <div className="text-xs">
                  <b>Archivage :</b>{' '}
                  {preview.actions.archive.slice(0, 5).map((a) => `${a.name} (${a.reason})`).join(', ')}
                  {preview.actions.archive.length > 5 && '…'}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Fermer</Button>
            <Button
              onClick={runExecute} disabled={executing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="ye-execute-btn"
            >
              <CheckCircle size={14} className="mr-1.5" />
              {executing ? 'Exécution…' : 'Exécuter la promotion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const KPICard = ({ label, value, tone = 'default', testid }) => {
  const toneCls = {
    default: 'border-[#E2E8F0]',
    ok: 'border-emerald-200 bg-emerald-50/40 text-emerald-800',
    warn: 'border-amber-200 bg-amber-50/40 text-amber-800',
    danger: 'border-red-200 bg-red-50/40 text-red-800',
  }[tone];
  return (
    <div className={`border rounded p-3 ${toneCls}`} data-testid={testid}>
      <p className="text-[10px] uppercase opacity-70">{label}</p>
      <p className="font-outfit text-2xl font-bold">{value ?? 0}</p>
    </div>
  );
};

export default YearEndPromotion;
